// src/pages/Profile.js
// Account settings: avatar, username, language, NSFW toggle, 18+ verify,
// and delete account.  PATCH /settings · PATCH /users/verify-age · DELETE /auth/delete-account

import { useState } from "react";
import Avatar from "../components/Avatar";
import { auth, clearToken } from "../api";

// file → raw base64 (no data: prefix) for the backend's R2 upload
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

const LEGAL_LINKS = [
  ["terms", "Terms of Use"],
  ["privacy", "Privacy Policy"],
  ["eula", "End User License Agreement"],
  ["content-policy", "Content Policy"],
  ["refund", "Refund & Cancellation"],
];

export default function Profile({ user, onUser, onLoggedOut, onOpenLegal }) {
  const [username, setUsername] = useState(user?.username || "");
  const [dob, setDob] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const ageVerified = user?.age_verified;

  const patch = async (payload, note) => {
    setErr("");
    setMsg("");
    setBusy(true);
    try {
      const updated = await auth.updateSettings(payload);
      onUser?.((u) => ({ ...u, ...updated }));
      setMsg(note || "Saved.");
    } catch (e) {
      setErr(e.detail?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setAvatarPreview(URL.createObjectURL(file));
      await patch({ profile_picture_base64: b64 }, "Avatar updated.");
    } catch (e2) {
      setErr(e2.message);
    }
  };

  const toggleNsfw = async () => {
    if (!ageVerified) {
      setErr("You must verify you're 18+ before enabling NSFW.");
      return;
    }
    await patch({ nsfw_enabled: !user?.nsfw_enabled }, "Preference saved.");
  };

  const verifyAge = async () => {
    if (!dob) return setErr("Enter your date of birth.");
    setErr("");
    setBusy(true);
    try {
      const r = await auth.verifyAge(dob);
      onUser?.((u) => ({ ...u, age_verified: r.age_verified }));
      setMsg("Age verified.");
    } catch (e) {
      setErr(e.detail?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(
      "Delete your account permanently?\n\n" +
      "This removes your chats, generated media and the characters you created — " +
      "those characters will no longer be available to anyone.\n\nThis cannot be undone."
    )) return;
    try {
      await auth.deleteAccount();
      clearToken();
      onLoggedOut?.();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 560 }}>
      <div className="page__head">
        <h1>Profile &amp; settings</h1>
      </div>

      <div className="profile__avatar">
        {avatarPreview ? <img src={avatarPreview} alt="avatar" /> : <Avatar name={username} size={88} />}
        <label className="btn btn--ghost">
          Change avatar
          <input type="file" accept="image/*" onChange={onAvatar} hidden />
        </label>
      </div>

      {msg && <div className="form-note">{msg}</div>}
      {err && <div className="form-error">{err}</div>}

      <div className="field">
        <span>Username</span>
        <div className="row-input">
          <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} />
          <button className="btn btn--primary" disabled={busy || !username.trim() || username === user?.username} onClick={() => patch({ username: username.trim() }, "Username updated.")}>
            Save
          </button>
        </div>
      </div>

      <div className="setting-row">
        <div>
          <div className="setting-row__title">NSFW content</div>
          <div className="setting-row__sub">{ageVerified ? "Requires a Premium plan to use in chat." : "Verify 18+ first."}</div>
        </div>
        <button className={`toggle__knob ${user?.nsfw_enabled ? "on" : ""}`} onClick={toggleNsfw} aria-label="Toggle NSFW" />
      </div>

      {!ageVerified && (
        <div className="setting-row" style={{ flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="setting-row__title">Verify your age</div>
            <div className="setting-row__sub">Enter your date of birth (18+).</div>
          </div>
          <div className="row-input">
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            <button className="btn btn--primary" disabled={busy} onClick={verifyAge}>Verify</button>
          </div>
        </div>
      )}

      <div className="setting-row" style={{ display: "block" }}>
        <div className="setting-row__title" style={{ marginBottom: 10 }}>Legal &amp; policies</div>
        <div className="legal__inline">
          {LEGAL_LINKS.map(([k, label]) => (
            <button key={k} className="inline-link" onClick={() => onOpenLegal?.(k)}>{label}</button>
          ))}
        </div>
      </div>

      <div className="danger">
        <div>
          <div className="setting-row__title">Delete account</div>
          <div className="setting-row__sub">Permanently removes your data, including the characters you created — they will no longer be available to anyone.</div>
        </div>
        <button className="btn btn--danger" onClick={del}>Delete</button>
      </div>
    </div>
  );
}
